import { PHONE_VIEWPORT_QUERY } from "@/hooks/use-phone-viewer-layout";
import {
  FaroRichTextEditor,
  grey,
  NoTranslate,
  Option,
} from "@faro-lotv/flat-ui";
import { useTranslate } from "@faro-lotv/foreign-observers";
import { assert } from "@faro-lotv/foundation";
import { Divider, Stack } from "@mui/material";
import {
  PropsWithChildren,
  useCallback,
  useMemo,
  useState,
  type JSX,
} from "react";
import {
  AnnotationEditorButtons,
  AnnotationEditorButtonsProps,
} from "./annotation-editor-buttons";
import {
  AnnotationEditorHeader,
  AnnotationEditorHeaderProps,
} from "./annotation-editor-header";
import {
  AnnotationEditorMetaFields,
  AnnotationEditorMetaFieldsProps,
} from "./annotation-editor-meta-fields";
import { AnnotationEditorTypeSelection } from "./annotation-editor-type-selection";

/** The maximum allowed number of characters in ProjectAPI for description of the annotation */
const MAX_DESCRIPTION_LENGTH = 10000;

export type AnnotationTypeDropdownProps = {
  /** The annotation type that should be selected in the dropdown */
  selectedAnnotationType: string;

  /** Function called when the selected value of the annotation type dropdown field changes */
  onAnnotationTypeChange(value: string): void;

  /** List of the available annotation type that can be created */
  annotationTypeOptions: Option[];
};

export type AnnotationEditorProps = AnnotationEditorHeaderProps &
  AnnotationEditorMetaFieldsProps &
  AnnotationEditorButtonsProps &
  AnnotationTypeDropdownProps & {
    /**
     * Initial value of the description text field.
     * Updating this prop from parent will have no effect!
     */
    initialDescription?: string;

    /** Function called when the content of the description text field changes */
    onDescriptionChange?(description?: string): void;

    /**
     * Method to call if there's an error with the description
     */
    onDescriptionError(error: Error): void;

    /**
     * If `true`, it indicates that the annotation type dropdown should be displayed otherwise it should be hidden.
     */
    showAnnotationTypeDropdown?: boolean;
  };

/**
 * @returns a dialog used to create and edit annotations and their fields value
 */
export function AnnotationEditor({
  children,
  title,
  initialDescription = "",
  assignee = "",
  initialDate,
  status = "",
  selectedAnnotationType,
  assigneeOptions,
  statusOptions,
  annotationTypeOptions,
  tagOptions,
  showConfirmButtonSpinner,
  confirmButtonText,
  attachments,
  tags,
  addNewAttachment,
  onTitleChange,
  onDescriptionChange,
  onAssigneeChange,
  onDueDateChange,
  onStatusChange,
  onAnnotationTypeChange,
  onTagsChange,
  onManageTagsClick,
  onCancelButtonClick,
  onConfirmButtonClick,
  onAttachmentOpened,
  showAnnotationTypeDropdown = false,
  autoFocusTitle = false,
  shouldShowAttachments,
  disabled,
  progress,
  onDescriptionError,
}: PropsWithChildren<AnnotationEditorProps>): JSX.Element {
  const [descriptionLength, setDescriptionLength] = useState(
    initialDescription.length,
  );

  const [titleErrorMessage, setTitleErrorMessage] = useState<
    string | undefined
  >();

  const handleDescriptionChange = useCallback(
    (newDescription: string) => {
      setDescriptionLength(newDescription.length);
      onDescriptionChange?.(newDescription);
    },
    [onDescriptionChange],
  );

  if (showAnnotationTypeDropdown) {
    assert(
      annotationTypeOptions.length > 0,
      "The annotationTypeOptions should not be empty when showAnnotationTypeDropdown is true",
    );
  }

  const finalAnnotationTypeOptions = useMemo(() => {
    assert(annotationTypeOptions, "annotationTypeOptions is invalid");

    return annotationTypeOptions.map((o) => ({
      ...o,
      label: <NoTranslate>{o.label}</NoTranslate>,
    }));
  }, [annotationTypeOptions]);

  const translate = useTranslate();

  return (
    <Stack
      direction="column"
      sx={{
        width: "522px",
        backgroundColor: grey[1000],
        p: 2,
        borderRadius: "4px",
        pointerEvents: disabled ? "none" : undefined,
        flexGrow: 1,
        gap: 3,
        // Allow the main content to scroll if there's not enough space
        overflowY: "auto",
        // but do not add an horizontal scrollbar when the main content needs to scroll
        overflowX: "clip",
        [`@media ${PHONE_VIEWPORT_QUERY}`]: {
          width: "min(522px, calc(100dvw - 32px))",
          maxWidth: "100%",
          maxHeight: "calc(100dvh - 32px)",
          pt: "max(16px, env(safe-area-inset-top, 0px))",
          pb: "max(16px, env(safe-area-inset-bottom, 0px))",
          boxSizing: "border-box",
          minHeight: 0,
          overflowY: "hidden",
          gap: 2,
          "& > *": { flexShrink: 0 },
          "& button": { minHeight: 44 },
        },
      }}
    >
      {showAnnotationTypeDropdown && (
        <>
          <AnnotationEditorTypeSelection
            value={selectedAnnotationType}
            options={finalAnnotationTypeOptions}
            onChange={(e) => onAnnotationTypeChange(e.target.value)}
            disabled={disabled}
          />
          <Divider sx={{ bgcolor: grey[0] }} />
        </>
      )}
      <AnnotationEditorHeader
        title={title}
        autoFocusTitle={autoFocusTitle}
        onTitleChange={onTitleChange}
        errorMessage={titleErrorMessage}
        onSetErrorMessage={setTitleErrorMessage}
        disabled={disabled}
      />
      <Stack
        sx={{
          // Add a scrollbar only to the main content if needed
          overflow: "auto",
          flexGrow: 1,
          gap: 3,
          mx: -0.875,
          px: 0.875,
          [`@media ${PHONE_VIEWPORT_QUERY}`]: {
            minHeight: 0,
            flexShrink: 1,
            mx: 0,
            px: 0,
            overflowX: "hidden",
            "& > *": { flexShrink: 0, minWidth: 0 },
          },
        }}
      >
        <FaroRichTextEditor
          initialText={initialDescription}
          dark
          fullWidth
          readOnly={disabled}
          label="Description"
          placeholder={translate("Insert a description")}
          onError={onDescriptionError}
          onChange={handleDescriptionChange}
          sx={{
            height: "120px",
          }}
          error={
            descriptionLength > MAX_DESCRIPTION_LENGTH
              ? "Maximum character limit exceeded"
              : undefined
          }
        />

        {children}
        <AnnotationEditorMetaFields
          disabled={disabled}
          assignee={assignee}
          initialDate={initialDate}
          status={status}
          assigneeOptions={assigneeOptions}
          statusOptions={statusOptions}
          tagOptions={tagOptions}
          attachments={attachments}
          tags={tags}
          addNewAttachment={addNewAttachment}
          onManageTagsClick={onManageTagsClick}
          onAssigneeChange={onAssigneeChange}
          onDueDateChange={onDueDateChange}
          onStatusChange={onStatusChange}
          onTagsChange={onTagsChange}
          shouldShowAttachments={shouldShowAttachments}
          progress={progress}
          onAttachmentOpened={onAttachmentOpened}
        />
      </Stack>
      <AnnotationEditorButtons
        disabledConfirmButton={
          !!titleErrorMessage || descriptionLength > MAX_DESCRIPTION_LENGTH
        }
        disabled={disabled}
        showConfirmButtonSpinner={showConfirmButtonSpinner}
        confirmButtonText={confirmButtonText}
        onCancelButtonClick={onCancelButtonClick}
        onConfirmButtonClick={onConfirmButtonClick}
      />
    </Stack>
  );
}
